import { ContractField } from 'src/entities/Contract';
import { FieldType, InputType } from 'src/services/api';
import { SignaturePageComponent } from 'src/store/signaturePage/types';
import {
  getComponentNameByComponentType,
  getComponentTypeUsingContractType,
} from 'src/utils/ContractUtils';

export const toSignaturePageComponent = (
  field: ContractField,
  value: string | null | undefined,
  recipientId: string,
): SignaturePageComponent => ({
  fieldType: field.type,
  inputType: field.inputType,
  autoFillField: field.autoFillField,
  xPosition: field.bounds.x,
  yPosition: field.bounds.y,
  label: field.label,
  receiverUserId: recipientId,
  height: field.bounds.height,
  width: field.bounds.width,
  isAgree: false,
  key: field.id,
  resizedHeight: field.bounds.resizedHeight,
  resizedWidth: field.bounds.resizedWidth,
  value: value ?? undefined,
  page: field.page,
  componentType: getComponentTypeUsingContractType(
    field,
    field.inputType === InputType.Client,
  ),
  name: getComponentNameByComponentType(field),
  isOptional: field.isOptional,
  imageTextValue:
    field.type === FieldType.Signature || field.type === FieldType.Initials
      ? field.value
      : '',
  encodedImgValue: field.encodedImgValue,
});

export const reconcileAutofillContractComponents = ({
  contractFields,
  currentComponents,
  getInputValue,
  recipientId,
}: {
  contractFields: ContractField[];
  currentComponents: SignaturePageComponent[];
  getInputValue: (field: ContractField) => string | null | undefined;
  recipientId: string;
}) => {
  const currentComponentsById = new Map(
    currentComponents.map((component) => [component.key, component]),
  );
  let hasChanges = false;

  const updatedComponents = contractFields.flatMap((field) => {
    const component = currentComponentsById.get(field.id);
    currentComponentsById.delete(field.id);

    if (field.inputType !== InputType.AutoFill) {
      return component ? [component] : [];
    }

    const value = getInputValue(field);
    if (!component) {
      if (!value) return [];

      hasChanges = true;
      return [toSignaturePageComponent(field, value, recipientId)];
    }

    const normalizedValue = value ?? undefined;
    if (normalizedValue === component.value) return [component];

    hasChanges = true;
    return [{ ...component, value: normalizedValue }];
  });
  updatedComponents.push(...currentComponentsById.values());

  return hasChanges ? updatedComponents : currentComponents;
};
